import {
  useDataProvider,
  useGetIdentity,
  useInvalidate,
  useNotification,
} from "@refinedev/core";
import { useQueryClient } from "@tanstack/react-query";

import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { Sentry } from "@/lib/sentry";
import type { IIdentity } from "@/providers/auth-provider";
import {
  VENUE_AUDIT_KINDS,
  type VenueAuditFindingRow,
  type VenueAuditKind,
  type VenueAuditRunResponse,
  type VenueAuditSeverity,
  type VenueAuditStatus,
} from "@/types/tina";

/** Cache key root for everything on this page that is not the Refine table itself. */
export const VENUE_AUDIT_QUERY_ROOT = "tina-venue-audit";

export type VenueAuditKindCounts = Record<VenueAuditKind, number>;

export interface VenueAuditCountScope {
  status: VenueAuditStatus;
  severity: VenueAuditSeverity | null;
}

/**
 * The two writes and the one derived read `/tina/venue-audit` needs, in the shape every
 * other Tina surface's repository hook takes: the data provider's `custom` for routes
 * Refine has no CRUD verb for, Sentry breadcrumbs around the mutation, and the error
 * surfaced through `toHumanErrorMessage` rather than swallowed.
 *
 * `/venue-audit/findings` itself is read by `useTable` through the `tina-venue-audit`
 * resource, not from here — the table owns pagination and `total`.
 */
export const useVenueAuditRepository = () => {
  const { custom } = useDataProvider()("tina");
  const { open } = useNotification();
  const invalidate = useInvalidate();
  const queryClient = useQueryClient();
  const { data: identity } = useGetIdentity<IIdentity>();

  /**
   * The signed-in curator's Firebase uid — the same value `resolveHitl` stamps as
   * `resolved_by` and `useEnrichmentIntake` as `requested_by`.
   *
   * `POST /venue-audit/findings/{id}/dismiss` authenticates the SERVICE, not the person,
   * so the body is the only place the actor can come from, and `dismissed_by` is
   * `min_length=1` — there is no "unknown" to fall back on. Writing anything but a uid
   * into an actor column is what put phantom "Former curators" on the IMS HITL
   * leaderboard, so an unloaded identity blocks the dismissal instead of inventing one.
   */
  const dismissedBy = identity?.id;

  /** Mark this page's rows and its count strip stale, so both refetch. */
  function invalidateFindings(): void {
    void invalidate({
      dataProviderName: "tina",
      resource: "tina-venue-audit",
      invalidates: ["list"],
    });
    void queryClient.invalidateQueries({ queryKey: [VENUE_AUDIT_QUERY_ROOT] });
  }

  async function dismiss(
    findingId: string,
    note: string | null,
  ): Promise<VenueAuditFindingRow | null> {
    if (!dismissedBy) {
      open?.({
        type: "error",
        message: "Cannot dismiss yet",
        description:
          "Your account has not finished loading, and a dismissal has to be attributable. Reload the page and try again.",
      });
      return null;
    }
    Sentry.addBreadcrumb({
      category: "tina-venue-audit.dismiss",
      message: "Dismissing a venue-audit finding",
      level: "info",
      data: { findingId, hasNote: Boolean(note) },
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: { dismissed_by: dismissedBy, note: note || null },
        meta: {
          urlPath: `venue-audit/findings/${encodeURIComponent(findingId)}/dismiss`,
        },
      });
      invalidateFindings();
      open?.({
        type: "success",
        message: "Finding dismissed",
        description: "It will not come back unless its venues change.",
      });
      return response.data as VenueAuditFindingRow;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to dismiss the finding",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  /**
   * How many findings each kind holds inside the current status/severity view.
   *
   * Nine requests with `limit=1`, reading `total` off each — the route counts over the same
   * WHERE it pages, so each total is exact. Counted rather than tallied from the loaded
   * page because the first real backlog is ~178 findings and a page holds 50: a client-side
   * tally would tell the curator that `non_venue_type` has 43 of them.
   */
  async function countsByKind({
    status,
    severity,
  }: VenueAuditCountScope): Promise<VenueAuditKindCounts> {
    const totals = await Promise.all(
      VENUE_AUDIT_KINDS.map(async (kind) => {
        const response = await custom!({
          url: "",
          method: "get",
          query: {
            status,
            kind,
            limit: 1,
            offset: 0,
            ...(severity ? { severity } : {}),
          },
          meta: { urlPath: "venue-audit/findings" },
        });
        const body = response.data as { total?: number };
        return [kind, body.total ?? 0] as const;
      }),
    );
    return Object.fromEntries(totals) as VenueAuditKindCounts;
  }

  /**
   * START the audit now, ignoring its once-a-day guard. Does not wait for it.
   *
   * The audit runs in tina's `tina-venue-audit` Cloud Run Job, because a
   * ~14,600-document Firestore read cannot finish inside a 60s HTTP request — so this
   * returns a Job execution, not counts. Read-only against Mood either way: the audit
   * re-reads the venue catalog, re-runs every check and writes only tina's own
   * findings table. It is how a curator confirms a venue they repaired has stopped being
   * a finding, without waiting for tomorrow.
   *
   * The three notifications match the three things that can happen, and none of them
   * claims a result: "started" is not "finished", and saying otherwise about a run whose
   * numbers nobody has seen yet is the same manufactured confidence the server-side
   * short-catalog floor exists to prevent.
   */
  async function runNow(): Promise<VenueAuditRunResponse> {
    Sentry.addBreadcrumb({
      category: "tina-venue-audit.run",
      message: "Starting the venue audit job",
      level: "info",
    });
    try {
      const response = await custom!({
        url: "",
        method: "post",
        payload: {},
        meta: { urlPath: "venue-audit/run" },
      });
      const outcome = response.data as VenueAuditRunResponse;
      // Invalidated even though nothing has changed YET: the run takes a couple of
      // minutes, so this only guarantees the table is not still showing a page cached
      // from before the curator's own last dismissal. The refresh that matters is the
      // one they do after the run finishes.
      invalidateFindings();
      if (outcome.kind === "dispatched") {
        open?.({
          type: "success",
          message: "Audit started",
          description:
            "It takes a couple of minutes. Refresh this page when it is done — nothing here updates on its own.",
        });
      } else if (outcome.kind === "noop") {
        open?.({
          type: "error",
          message: "Audit not started",
          description:
            "The audit job is not configured on this environment, so there was nothing to start.",
        });
      } else {
        open?.({
          type: "error",
          message: "Failed to start the audit",
          description:
            outcome.error_message ??
            "The audit job could not be started and gave no reason.",
        });
      }
      return outcome;
    } catch (error) {
      Sentry.captureException(error);
      open?.({
        type: "error",
        message: "Failed to start the audit",
        description: toHumanErrorMessage(error),
      });
      throw error;
    }
  }

  return { dismiss, countsByKind, runNow, dismissedBy };
};
